import React, { useState } from 'react';

export default function InternalTransfer({ accounts, onTransfer }) {
  const [fromAccount, setFromAccount] = useState('');
  const [toAccount, setToAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!fromAccount || !toAccount || !amount || !reference) {
      setError('Please fill in all fields');
      return;
    }

    if (fromAccount === toAccount) {
      setError('Cannot transfer to the same account');
      return;
    }

    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount) || transferAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const sourceAccount = accounts.find(acc => acc.type === fromAccount);
    if (transferAmount > sourceAccount.available) {
      setError('Insufficient funds');
      return;
    }

    setLoading(true);
    try {
      await onTransfer({
        fromAccount,
        toAccount,
        amount: transferAmount,
        reference,
        transferType: 'internal'
      });

      // Reset form
      setFromAccount('');
      setToAccount('');
      setAmount('');
      setReference('');
    } catch (error) {
      setError('Transfer failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              From Account
            </label>
            <select
              value={fromAccount}
              onChange={(e) => setFromAccount(e.target.value)}
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              disabled={loading}
            >
              <option value="">Select account</option>
              {accounts.map((account) => (
                <option key={account.accountNumber} value={account.type}>
                  {account.type} - {formatCurrency(account.available)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              To Account
            </label>
            <select
              value={toAccount}
              onChange={(e) => setToAccount(e.target.value)}
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              disabled={loading}
            >
              <option value="">Select account</option>
              {accounts.map((account) => (
                <option key={account.accountNumber} value={account.type}>
                  {account.type} - {formatCurrency(account.available)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Amount
          </label>
          <div className="relative mt-1 rounded-xl shadow-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="block w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
              disabled={loading}
              min="0.01"
              step="0.01"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Reference
          </label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="What's this transfer for?"
            className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
              focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
              hover:bg-white transition-colors duration-200
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm"
            disabled={loading}
          />
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl 
              shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
              disabled:bg-gray-300 disabled:cursor-not-allowed
              transition-colors duration-200"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Transfer Money'
            )}
          </button>
        </div>
      </form>
    </div>
  );
} 